import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { complianceStandardsApi } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Loader2, Shield, ChevronRight, AlertTriangle } from 'lucide-react';

const segments = [
  { key: 'compliant',     label: 'Compliant',     cls: 'bg-emerald-500', text: 'text-emerald-600' },
  { key: 'at_risk',       label: 'At risk',       cls: 'bg-amber-500',   text: 'text-amber-600' },
  { key: 'non_compliant', label: 'Non-compliant', cls: 'bg-red-500',     text: 'text-red-600' },
  { key: 'unmonitored',   label: 'Unmonitored',   cls: 'bg-muted',       text: 'text-muted-foreground' },
];

const DashboardComplianceCard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    complianceStandardsApi
      .dashboardOverview()
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 flex justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }
  if (!data) return null;

  const total = data.total_assets || 0;
  if (total === 0) return null;

  const counts = {
    compliant: data.compliant || 0,
    at_risk: data.at_risk || 0,
    non_compliant: data.non_compliant || 0,
    unmonitored: data.unmonitored || 0,
  };

  // Headline metric: monitored compliance percentage. Unmonitored is shown
  // separately so it doesn't dilute the headline number.
  const headlinePct = data.compliant_pct != null ? data.compliant_pct : null;
  const worst = data.non_compliant_assets_sample || [];

  return (
    <Card data-testid="dashboard-compliance-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <CardTitle className="text-sm uppercase tracking-wide flex items-center gap-2">
            <Shield className="w-4 h-4 text-[#C9A400]" />
            Fleet Compliance
          </CardTitle>
          <button
            className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
            onClick={() => navigate('/compliance-standards')}
            data-testid="dashboard-compliance-view-all"
          >
            Manage standards <ChevronRight className="w-3 h-3" />
          </button>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Headline */}
        <div className="flex items-end gap-6 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Monitored fleet compliance</div>
            <div
              className={`text-4xl font-mono font-bold mt-1 ${
                headlinePct == null
                  ? 'text-muted-foreground'
                  : headlinePct >= 90
                  ? 'text-emerald-600'
                  : headlinePct >= 70
                  ? 'text-amber-600'
                  : 'text-red-600'
              }`}
              data-testid="dashboard-compliance-headline"
            >
              {headlinePct == null ? '—' : `${headlinePct}%`}
            </div>
            <div className="text-[11px] text-muted-foreground mt-1">
              {counts.compliant} of {data.monitored} monitored assets fully compliant
            </div>
          </div>
          <div className="flex-1 min-w-[200px]">
            {/* Stacked bar */}
            <div className="h-2 rounded-full overflow-hidden flex bg-[#F0EDE6]">
              {segments.map((seg) => {
                const c = counts[seg.key];
                const pct = total ? (c / total) * 100 : 0;
                if (pct === 0) return null;
                return (
                  <div
                    key={seg.key}
                    className={seg.cls}
                    style={{ width: `${pct}%` }}
                    title={`${seg.label}: ${c}`}
                  />
                );
              })}
            </div>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[10px]">
              {segments.map((seg) => (
                <span key={seg.key} className={`flex items-center gap-1 ${seg.text}`}>
                  <span className={`w-2 h-2 rounded-full ${seg.cls}`} />
                  <span className="font-mono">{counts[seg.key]}</span>
                  <span className="uppercase tracking-wide">{seg.label}</span>
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Worst-offenders drill-down */}
        {worst.length > 0 && (
          <div className="rounded-sm border border-border bg-[#FBFAF7]">
            <div className="px-3 py-2 border-b border-border text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
              <AlertTriangle className="w-3 h-3 text-red-600" />
              Worst-performing assets
            </div>
            <ul className="divide-y divide-border">
              {worst.slice(0, 5).map((a) => (
                <li
                  key={a.asset_id}
                  className="flex items-center justify-between gap-3 px-3 py-2 hover:bg-[#F2F0EA] cursor-pointer"
                  onClick={() => navigate(`/assets?view=${a.asset_id}`)}
                  data-testid={`dashboard-compliance-asset-${a.asset_id}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span
                      className={`w-9 h-9 rounded-full border flex items-center justify-center text-xs font-mono font-bold shrink-0 ${
                        a.score >= 50
                          ? 'border-[#FDE68A] bg-[#FFFBEB] text-[#B45309]'
                          : 'border-[#FECACA] bg-[#FEF2F2] text-[#B91C1C]'
                      }`}
                    >
                      {a.score}
                    </span>
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{a.asset_name}</div>
                      <div className="text-[10px] text-muted-foreground">
                        {a.missing > 0 && `${a.missing} missing cert(s)`}
                        {a.missing > 0 && (a.expired > 0 || a.overdue_inspections > 0) && ' · '}
                        {a.expired > 0 && `${a.expired} expired`}
                        {a.expired > 0 && a.overdue_inspections > 0 && ' · '}
                        {a.overdue_inspections > 0 && `${a.overdue_inspections} overdue inspection(s)`}
                      </div>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                </li>
              ))}
            </ul>
          </div>
        )}

        {counts.unmonitored > 0 && (
          <p className="text-[11px] text-muted-foreground">
            <strong className="text-foreground">{counts.unmonitored}</strong> asset(s) have no compliance standards
            linked yet — they won't appear in this score until you assign at least one.
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default DashboardComplianceCard;
